import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { DismantlingBayType, IDismantlingBay } from "../../models/DismantlingBay";

const colHelper = createColumnHelper<IDismantlingBay>();

const typeLabel = (type: DismantlingBayType) => {
  switch (type) {
    case DismantlingBayType.ProductionLine:
      return "Production Line";
    case DismantlingBayType.Bay:
    default:
      return "Bay";
  }
};

const cols = [
  colHelper.display({
    id: "name",
    header: "Name",
    cell: (info) => <div className="ml-4">{info.row.original.name}</div>,
  }),
  colHelper.display({
    id: "type",
    header: "Type",
    cell: (info) => <span>{typeLabel(info.row.original.type)}</span>,
  }),
  colHelper.display({
    id: "capacity",
    header: "Capacity",
    cell: (info) => <span>{info.row.original.capacity}</span>,
  }),
  colHelper.display({
    id: "easy",
    header: "Easy",
    cell: (info) => <span>{info.row.original.easy}</span>,
  }),
  colHelper.display({
    id: "medium",
    header: "Medium",
    cell: (info) => <span>{info.row.original.medium}</span>,
  }),
  colHelper.display({
    id: "hard",
    header: "Hard",
    cell: (info) => <span>{info.row.original.hard}</span>,
  }),
];

export default function DismantlingBaysTable({
  dismantlingBays,
  setDismantlingBay,
}: {
  dismantlingBays: IDismantlingBay[];
  setDismantlingBay: (dismantlingBay: IDismantlingBay) => void;
}) {
  const table = useReactTable({
    data: dismantlingBays,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder
                    ? null
                    : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => {
            const rowCells = row.getVisibleCells();
            return (
              <tr
                key={row.id}
                onClick={() => setDismantlingBay(row.original)}
                className="hover:bg-gray-100 hover:cursor-pointer"
              >
                {rowCells.map((cell) => (
                  <td key={cell.id} className="px-3 py-3 border text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {dismantlingBays.length === 0 && (
            <tr>
              <td colSpan={cols.length} className="px-3 py-6 text-center text-sm text-gray-500">
                No dismantling bays yet. Add one to get started.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
